import { createContext, type PropsWithChildren, useCallback, useContext, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  type kycMutations,
  type KycProvider,
  kycQueryKeys,
} from '@repo/api-kit/queries/due-diligence/kyc';
import { useKycTracking } from '#tracking/use-kyc-tracking';

export interface KycProviderProps {
  /** Organization's legal country code (e.g. `"FR"`, `"DE"`).
   * Used as market parameter for API calls when `isRegisterFlow` is true,
   * and to select the appropriate loading UI (IDnow for `"DE"`, Fourthline otherwise). */
  legalCountry: string;
  /** Current membership ID. Used only for analytics tracking and Sentry error context. */
  membershipId?: string;
  /** Current user ID. Used only for analytics tracking. */
  userId?: string;
  /** Prefix for all Segment tracking events (e.g. `"verification"`, `"onboarding_partner"`).
   * Events are emitted as `${trackingPrefix}_kyc_started`, `${trackingPrefix}_kyc_succeeded`, etc. */
  trackingPrefix: string;
  /** When provided, the success screen shows a link to this URL instead of calling `onComplete`.
   * Used when the KYC flow is embedded and needs to redirect externally on completion. */
  completionRedirectUrl?: string;
  /** Global error handler called on unexpected errors (ErrorBoundary, SMS failures).
   * Allows the parent app to log or report errors centrally. */
  onError?: (error: Error) => void;
  /** Called when the user presses back in a provider's UI (IDnow or Fourthline). */
  onBack?: () => void;
  /** Main completion callback. Called when KYC is skipped (already completed) or
   * when the user finishes the verification flow.
   * Receives the provider and outcome so callers can record telemetry attributes. */
  onComplete: (provider: keyof typeof KycProvider, outcome?: 'completed' | 'skipped') => void;
  /** Whether the user holds a German residence permit. Passed to the IDnow provider
   * to adjust identity verification requirements. */
  hasGermanResidencePermit: boolean;
  /** Enables register-flow-specific behavior:
   * - API calls include `legalCountry` as market parameter
   * - KYC is only shown when process status is `pending_liveness`
   * - All other statuses are skipped (handled elsewhere in the register flow) */
  isRegisterFlow?: boolean;
  /** Origin identifier for tracking (e.g. "accounts_page", "card_flow"). Passed through to Segment events. */
  trackingEventOrigin?: string | null;
  /** Optional override for the SMS mutation used by IdnowProvider.
   * A mutation-options factory matching `kycMutations.sendVerificationSms`.
   * When provided, IdnowProvider uses it instead of the built-in mutation.
   * Needed when the host app uses a different auth context (e.g. register). */
  sendSmsMutation?: () => ReturnType<typeof kycMutations.sendVerificationSms>;
  /** Whether the loading skeleton should fill the viewport height. Default `true`.
   * Set to `false` when `KycRouter` is embedded in a height-constrained container
   * (e.g. a modal), where a full-height skeleton would cause it to overflow/scroll. */
  isFullHeight?: boolean;
}

interface KycContextValue extends KycProviderProps {}

const KycContext = createContext<KycContextValue | null>(null);

export function KycContextProvider({ children, ...props }: PropsWithChildren<KycProviderProps>) {
  const { onComplete, onError, membershipId, userId, trackingPrefix, trackingEventOrigin } = props;
  const queryClient = useQueryClient();

  const events = useKycTracking({
    trackingPrefix,
    membershipId,
    userId,
    trackingEventOrigin: trackingEventOrigin ?? null,
  });

  const handleOnComplete = useCallback(
    (provider: keyof typeof KycProvider, outcome: 'completed' | 'skipped' = 'completed') => {
      events.kycSucceeded({ provider: String(provider) });
      void queryClient.invalidateQueries({ queryKey: kycQueryKeys.item() });
      onComplete(provider, outcome);
    },
    [onComplete, queryClient, events]
  );

  const handleOnError = useCallback(
    (error: Error) => {
      onError?.(error);
    },
    [onError]
  );

  const value = useMemo<KycContextValue>(
    () => ({
      legalCountry: props.legalCountry,
      membershipId: props.membershipId,
      userId: props.userId,
      trackingPrefix: props.trackingPrefix,
      completionRedirectUrl: props.completionRedirectUrl,
      onError: handleOnError,
      onBack: props.onBack,
      onComplete: handleOnComplete,
      hasGermanResidencePermit: props.hasGermanResidencePermit,
      isRegisterFlow: props.isRegisterFlow,
      trackingEventOrigin: props.trackingEventOrigin ?? null,
      sendSmsMutation: props.sendSmsMutation,
      isFullHeight: props.isFullHeight,
    }),
    [
      props.legalCountry,
      props.membershipId,
      props.userId,
      props.trackingPrefix,
      props.completionRedirectUrl,
      handleOnError,
      props.onBack,
      props.hasGermanResidencePermit,
      props.isRegisterFlow,
      props.trackingEventOrigin,
      props.sendSmsMutation,
      handleOnComplete,
      props.isFullHeight,
    ]
  );

  return <KycContext.Provider value={value}>{children}</KycContext.Provider>;
}

export function useKycContext(): KycContextValue {
  const context = useContext(KycContext);
  if (!context) {
    throw new Error('useKycContext must be used within a KycProvider');
  }
  return context;
}

export function useMaybeKycContext(): KycContextValue | null {
  return useContext(KycContext);
}
